import { createTamagui, createMedia } from 'tamagui';
import { createFont } from '@tamagui/core';
import { shorthands } from '@tamagui/shorthands';
import { createTokens } from '@tamagui/core';
import { createAnimations } from '@tamagui/animations-css';
import { Platform } from 'react-native';

const animations = createAnimations({
  quick: 'ease-in 150ms',
  medium: 'ease-in 300ms',
  slow: 'ease-in 450ms',
  bouncy: 'ease-in-out 300ms',
  lazy: 'ease-in 600ms',
  tooltip: 'ease-in 100ms',
});

const media = createMedia({
  xs: { maxWidth: 660 },
  sm: { maxWidth: 800 },
  md: { maxWidth: 1020 },
  lg: { maxWidth: 1280 },
  xl: { maxWidth: 1420 },
  xxl: { maxWidth: 1600 },
  gtXs: { minWidth: 660 + 1 },
  gtSm: { minWidth: 800 + 1 },
  gtMd: { minWidth: 1020 + 1 },
  gtLg: { minWidth: 1280 + 1 },
  short: { maxHeight: 820 },
  tall: { minHeight: 820 },
  hoverNone: { hover: 'none' },
  pointerCoarse: { pointer: 'coarse' },
});

// Primary Color: #2a7cff (Dodger Blue)
// Dark Color: #2c303b (Dark Slate Gray)
// Default Tamagui color tokens
const tokens = createTokens({
  color: {
    white: '#ffffff',
    black: '#000000',
    primary: '#2a7cff',
    primaryDark: '#1a5ae6',
    primaryLight: '#5a9cff',
    dark: '#2c303b',
    darkLight: '#3a3f4d',
    darkDarker: '#1a1f2b',
    blue1: '#f0f9ff',
    blue2: '#e0f2fe',
    blue3: '#bae6fd',
    blue4: '#7dd3fc',
    blue5: '#38bdf8',
    blue6: '#2a7cff',
    blue7: '#1a5ae6',
    blue8: '#1247d9',
    blue9: '#0a39cc',
    blue10: '#0230bf',
    blue11: '#021a99',
    blue12: '#010d4d',
    gray1: '#fcfcfc',
    gray2: '#f9f9f9',
    gray3: '#f0f0f0',
    gray4: '#e8e8e8',
    gray5: '#e0e0e0',
    gray6: '#d1d1d1',
    gray7: '#b4b4b4',
    gray8: '#8b8b8b',
    gray9: '#6b6b6b',
    gray10: '#4d4d4d',
    gray11: '#2e2e2e',
    gray12: '#1a1a1a',
    green1: '#f0fdf4',
    green2: '#dcfce7',
    green3: '#bbf7d0',
    green4: '#86efac',
    green5: '#4ade80',
    green6: '#22c55e',
    green7: '#16a34a',
    green8: '#15803d',
    green9: '#166534',
    green10: '#14532d',
    green11: '#052e16',
    green12: '#021f0b',
    red1: '#fef2f2',
    red2: '#fee2e2',
    red3: '#fecaca',
    red4: '#fca5a5',
    red5: '#f87171',
    red6: '#ef4444',
    red7: '#dc2626',
    red8: '#b91c1c',
    red9: '#991b1b',
    red10: '#7f1d1d',
    red11: '#450a0a',
    red12: '#2d0606',
    yellow1: '#fefce8',
    yellow2: '#fef9c3',
    yellow3: '#fef08a',
    yellow4: '#fde047',
    yellow5: '#facc15',
    yellow6: '#eab308',
    yellow7: '#ca8a04',
    yellow8: '#a16207',
    yellow9: '#854d0e',
    yellow10: '#713f12',
    yellow11: '#422006',
    yellow12: '#2e1803',
  },
  space: {
    0: 0,
    1: 6,
    2: 12,
    3: 18,
    4: 24,
    5: 30,
    6: 36,
    7: 42,
    8: 48,
    9: 56,
    10: 64,
    11: 72,
    12: 80,
    true: 24,
  },
  size: {
    0: 0,
    1: 6,
    2: 12,
    3: 18,
    4: 24,
    5: 30,
    6: 36,
    7: 42,
    8: 48,
    9: 56,
    10: 64,
    11: 72,
    12: 80,
    true: 24,
  },
  radius: {
    0: 0,
    1: 2,
    2: 4,
    3: 6,
    4: 8,
    5: 12,
    6: 16,
  },
  zIndex: {
    0: 0,
    1: 100,
    2: 200,
    3: 300,
    4: 400,
    5: 500,
  },
});

// Modern light theme with white background
const lightTheme = {
  background: tokens.color.white,
  backgroundHover: '#f8f9fa',
  backgroundPress: '#f0f2f5',
  backgroundFocus: '#f8f9fa',
  color: tokens.color.dark,
  colorHover: tokens.color.darkLight,
  colorPress: tokens.color.darkDarker,
  colorFocus: tokens.color.dark,
  borderColor: '#e0e6ed',
  borderColorHover: '#d0d6dd',
  borderColorPress: '#c0c6cd',
  borderColorFocus: tokens.color.primary,
  placeholderColor: '#999999',
  shadowColor: 'rgba(0, 0, 0, 0.08)',
  ...(Platform.OS === 'android' || Platform.OS === 'ios' ? { fontFamily: 'OpenSans_400Regular' } : {}),
};

// Modern dark theme
const darkTheme = {
  background: tokens.color.dark,
  backgroundHover: tokens.color.darkLight,
  backgroundPress: '#4a4f5d',
  backgroundFocus: tokens.color.darkLight,
  color: tokens.color.white,
  colorHover: '#f0f2f5',
  colorPress: '#e0e6ed',
  colorFocus: tokens.color.white,
  borderColor: tokens.color.darkLight,
  borderColorHover: '#4a4f5d',
  borderColorPress: '#5a5f6d',
  borderColorFocus: tokens.color.primary,
  placeholderColor: '#999999',
  shadowColor: 'rgba(0, 0, 0, 0.3)',
  ...(Platform.OS === 'android' || Platform.OS === 'ios' ? { fontFamily: 'OpenSans_400Regular' } : {}),
};

// Open Sans and Segoe UI font configuration with platform-specific weight mapping
const isNative = Platform.OS !== 'web';

// For native, we use the specific font family names with weights baked in
// For web, we use CSS font-family with font-weight
const openSansFont = createFont({
  family: isNative ? 'OpenSans_400Regular' : "'Open Sans', 'Segoe UI', system-ui, -apple-system, sans-serif",
  size: {
    1: 12,
    2: 14,
    3: 16,
    4: 18,
    5: 20,
    6: 24,
    7: 28,
    8: 32,
    9: 40,
    10: 48,
  },
  lineHeight: {
    1: 16,
    2: 20,
    3: 24,
    4: 26,
    5: 28,
    6: 32,
    7: 36,
    8: 40,
    9: 48,
    10: 56,
  },
  weight: {
    3: '300',
    4: '400',
    5: '500',
    6: '600',
    7: '700',
  },
  letterSpacing: {
    1: 0.25,
    2: 0.5,
    3: 0.75,
    4: 0.5,
    5: 0.5,
    6: 0.5,
    7: 0.25,
    8: 0,
    9: -0.5,
    10: -1,
  },
});

export const config = createTamagui({
  tokens,
  themes: {
    light: lightTheme,
    dark: darkTheme,
  },
  shorthands,
  media,
  animations,
  fonts: {
    heading: openSansFont,
    body: openSansFont,
  },
  defaultFont: 'body',
});

export const tamaguiConfig = config;

// Type exports for TypeScript
export type Conf = typeof config;

declare module 'tamagui' {
  interface TamaguiCustomConfig extends Conf {}
}
